// src/pages/ProductsPage.tsx
import React, { useState, useEffect } from "react";
import { products, getCategories } from "@/data/products";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Product } from "@/types";
import { Mail } from "lucide-react";

const ProductsPage: React.FC = () => {
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [sortOption, setSortOption] = useState<string>("default");
  
  // Initialize showEmailDialog based on sessionStorage
  const [showEmailDialog, setShowEmailDialog] = useState(() => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const dialogInteracted = sessionStorage.getItem("jogngym_email_dialog_interacted");
      return !dialogInteracted; // Show dialog if not interacted with yet (null or not 'true')
    }
    return true; // Default to true if sessionStorage is not available
  });
  
  const [email, setEmail] = useState("");
  const categories = getCategories();

  useEffect(() => {
    let result = [...products];
    
    // Filter by category
    if (selectedCategory !== "all") {
      result = result.filter(product => product.category === selectedCategory);
    }
    
    // Sort products
    switch (sortOption) {
      case "price-low-high":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-high-low":
        result.sort((a, b) => b.price - a.price);
        break;
      case "name-a-z":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-z-a":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }
    
    setFilteredProducts(result);
  }, [selectedCategory, sortOption]);

  const markDialogAsInteractedAndClose = () => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem("jogngym_email_dialog_interacted", "true");
    }
    setShowEmailDialog(false);
  };

  /**
   * Handles newsletter email submission by sending it to the backend.
   * This triggers the Behavioral Analysis Agent and subsequently the Personalized Engagement Agent.
   * @param e The form submission event.
   */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Attempting to submit email:", email);
    
    try {
      // Store email in session storage as identified user
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem("jogngym_user_email", email);
        sessionStorage.setItem("jogngym_product_view_count", "0"); // Initialize product view counter
      }
      
      const response = await fetch('/api/newsletter-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Newsletter signup successful:", result);

      if (typeof window !== 'undefined' && window.sessionStorage && email) {
        sessionStorage.setItem("jogngym_subscribed_email", email); // Store the submitted email locally
      }
      markDialogAsInteractedAndClose(); // Mark interaction and close dialog
      alert("Thanks for subscribing! Check your email for a welcome message from Jog N Gym."); // Simple user feedback
    } catch (error) {
      console.error("Error submitting email for newsletter:", error);
      alert("Failed to subscribe. Please try again later."); // Simple user feedback
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-8">Our Products</h1>
      
      {/* Filters and Sorting */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row justify-between gap-6">
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Filter by Category
            </label>
            <Select
              value={selectedCategory}
              onValueChange={(value) => setSelectedCategory(value)}
            >
              <SelectTrigger className="w-full bg-white border-slate-200">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Sort by
            </label>
            <Select
              value={sortOption}
              onValueChange={(value) => setSortOption(value)}
            >
              <SelectTrigger className="w-full bg-white border-slate-200">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="price-low-high">Price: Low to High</SelectItem>
                <SelectItem value="price-high-low">Price: High to Low</SelectItem>
                <SelectItem value="name-a-z">Name: A to Z</SelectItem>
                <SelectItem value="name-z-a">Name: Z to A</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-lg text-gray-600">No products found matching your criteria.</p>
        </div>
      )}

      {/* Email Dialog */}
      <Dialog 
        open={showEmailDialog} 
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            markDialogAsInteractedAndClose();
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Subscribe to Our Newsletter</DialogTitle>
            <DialogDescription>
              Get exclusive offers and updates about our latest products!
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEmailSubmit} className="space-y-4 mt-4">
            <div className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-slate-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={markDialogAsInteractedAndClose}>
                Maybe Later
              </Button>
              <Button type="submit" variant="primary">
                Subscribe
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;